/**
 * AstroJi Consultation REST API Client
 * Connects directly to Next.js API route handlers in astroji_consultation_dashboard (test.astroji.com/api/v1)
 * Eliminates direct Supabase table and remote Edge Function coupling from the storefront.
 */

import type { Expert } from "@/types/expert";
import type {
  ResolvedConsultationSession,
  ConsultationIntakeSubmissionResult,
  ConsultationSlotInventory,
  ConsultationSlotSelectionResult,
  ConsultationSlotHoldResult,
  ConsultationOrderResult,
  ConsultationPaymentVerificationResult,
  ConsultationIntakeDraft,
} from "./consultationClient";
import type { LandingAbAssignment } from "./landingAbTest";

export interface PricingRow {
  id: string;
  duration_minutes: number;
  amount: number;
  currency: string;
  buffer_time?: number | null;
  label: string | null;
  description: string | null;
  sort_order?: number;
  marketing_active?: boolean | null;
  metadata?: Record<string, any>;
}

export interface BookingSettingsRow {
  consultation_type: string;
  service_commitment_type: string;
  slot_start_hour: number;
  slot_end_hour: number;
  blocked_weekdays: number[] | null;
}

const getApiBaseUrl = () => {
  const envUrl = process.env.NEXT_PUBLIC_CONSULTATION_API_URL;
  if (envUrl) {
    return envUrl.replace(/\/$/, "");
  }
  return "https://test.astroji.com/api/v1";
};

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

  const defaultHeaders: Record<string, string> = {
    Accept: "application/json",
  };

  if (options?.body) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  const signal =
    options?.signal ||
    (typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
      ? AbortSignal.timeout(4000)
      : undefined);

  const response = await fetch(url, {
    ...options,
    signal,
    headers: {
      ...defaultHeaders,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;
    try {
      const errorPayload = await response.json();
      if (errorPayload?.error) {
        errorMessage = String(errorPayload.error);
      }
    } catch {
      // Ignore json parse error
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  if (result && typeof result === "object" && "success" in result && !result.success) {
    throw new Error(result.error || "Request failed");
  }

  return result as T;
}

// ── Public Experts ──
export async function getPublicExperts(params?: { slug?: string }): Promise<Expert[]> {
  const searchParams = new URLSearchParams();
  if (params?.slug) searchParams.set("slug", params.slug);
  const qs = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const res = await fetchApi<{ success: boolean; data: Expert[] | Expert }>(`/public/experts${qs}`, {
    cache: "no-store",
  });
  if (Array.isArray(res.data)) {
    return res.data;
  }
  return res.data ? [res.data] : [];
}

export async function getPublicExpertBySlug(slug: string): Promise<Expert | null> {
  try {
    const res = await fetchApi<{ success: boolean; data: Expert }>(`/public/experts/${encodeURIComponent(slug)}`, {
      cache: "no-store",
    });
    return res.data || null;
  } catch (err) {
    console.error(`Failed to load expert by slug "${slug}":`, err);
    return null;
  }
}

// ── Public Pricing & Booking Settings ──
export async function getPublicPricing(params?: { marketingOnly?: boolean }): Promise<PricingRow[]> {
  const searchParams = new URLSearchParams();
  if (params?.marketingOnly !== undefined) {
    searchParams.set("marketingOnly", String(params.marketingOnly));
  }
  const qs = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const res = await fetchApi<{ success: boolean; data: PricingRow[] }>(`/public/pricing${qs}`, {
    cache: "no-store",
  });
  return res.data || [];
}

export async function getPublicBookingSettings(): Promise<BookingSettingsRow> {
  const res = await fetchApi<{ success: boolean; data: BookingSettingsRow }>("/public/booking-settings", {
    cache: "no-store",
  });
  return (
    res.data || {
      consultation_type: "personal_consultation",
      service_commitment_type: "expert_consultation",
      slot_start_hour: 11,
      slot_end_hour: 20,
      blocked_weekdays: [],
    }
  );
}

// ── Slots & Inventory ──
export async function getPublicSlots(params: {
  durationMinutes?: number;
  fromDate?: string;
  days?: number;
  consultationId?: number;
}): Promise<ConsultationSlotInventory> {
  const searchParams = new URLSearchParams();
  if (params.durationMinutes) searchParams.set("duration", String(params.durationMinutes));
  if (params.fromDate) searchParams.set("from_date", params.fromDate);
  if (params.days) searchParams.set("days", String(params.days));
  if (params.consultationId) searchParams.set("consultation_id", String(params.consultationId));

  const qs = searchParams.toString() ? `?${searchParams.toString()}` : "";
  return fetchApi<ConsultationSlotInventory>(`/public/slots${qs}`, {
    cache: "no-store",
  });
}

export async function selectPublicSlot(params: {
  token?: string;
  consultationId?: number;
  preferred_date: string;
  preferred_time: string;
  timezone?: string;
}): Promise<ConsultationSlotSelectionResult> {
  return fetchApi<ConsultationSlotSelectionResult>("/public/slots", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ── Slot Holds ──
export async function createPublicSlotHold(params: Record<string, unknown>): Promise<ConsultationSlotHoldResult> {
  return fetchApi<ConsultationSlotHoldResult>("/public/slot-holds", {
    method: "POST",
    body: JSON.stringify({
      action: "create_slot_hold",
      ...params,
    }),
  });
}

export async function releasePublicSlotHold(params: {
  holdId?: number;
  consultationId?: number;
}): Promise<{ success: boolean; holdId?: number | null; consultationId?: number | null }> {
  return fetchApi<{ success: boolean; holdId?: number | null; consultationId?: number | null }>(
    "/public/slot-holds",
    {
      method: "POST",
      body: JSON.stringify({
        action: "release_slot_hold",
        ...params,
      }),
    },
  );
}

// ── Checkout & Orders ──
export async function createPublicOrder(params: Record<string, unknown>): Promise<ConsultationOrderResult> {
  return fetchApi<ConsultationOrderResult>("/public/checkout", {
    method: "POST",
    body: JSON.stringify({
      action: "create_order",
      ...params,
    }),
  });
}

export async function verifyPublicPayment(params: {
  consultationId: number;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}): Promise<ConsultationPaymentVerificationResult> {
  return fetchApi<ConsultationPaymentVerificationResult>("/public/checkout", {
    method: "POST",
    body: JSON.stringify({
      action: "verify_payment",
      ...params,
    }),
  });
}

export async function updatePublicPaymentStatus(params: {
  consultationId: number;
  status: "payment_abandoned" | "payment_failed" | "pending_payment" | string;
}): Promise<ConsultationPaymentVerificationResult> {
  return fetchApi<ConsultationPaymentVerificationResult>("/public/checkout", {
    method: "POST",
    body: JSON.stringify({
      action: "update_status",
      ...params,
    }),
  });
}

export async function submitPublicQuiz(
  params: Record<string, unknown>,
): Promise<{ success: boolean; consultationId: number; message?: string }> {
  return fetchApi<{ success: boolean; consultationId: number; message?: string }>("/public/checkout", {
    method: "POST",
    body: JSON.stringify({
      action: "submit_quiz",
      ...params,
    }),
  });
}

// ── Consultation Session Resolution ──
export async function resolvePublicSession(params: {
  token?: string;
  phone?: string;
  consultationId?: number;
}): Promise<ResolvedConsultationSession> {
  return fetchApi<ResolvedConsultationSession>("/public/session", {
    method: "POST",
    body: JSON.stringify({
      action: "resolve",
      ...params,
    }),
  });
}

// ── Intake Submission ──
export async function submitPublicIntake(params: {
  token: string;
  apply_mode?: "shared" | "per_consultation";
  shared_details?: ConsultationIntakeDraft;
  consultation_details?: Array<{
    consultation_id: number;
    details: ConsultationIntakeDraft;
  }>;
  commit?: boolean;
}): Promise<ConsultationIntakeSubmissionResult> {
  return fetchApi<ConsultationIntakeSubmissionResult>("/public/intake", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ── A/B Testing ──
export async function assignPublicAbVariant(params: Record<string, unknown>): Promise<LandingAbAssignment> {
  return fetchApi<LandingAbAssignment>("/public/ab-test", {
    method: "POST",
    body: JSON.stringify({
      action: "assign",
      ...params,
    }),
  });
}

export async function trackPublicAbEvent(params: Record<string, unknown>): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>("/public/ab-test", {
    method: "POST",
    body: JSON.stringify({
      action: "track_event",
      ...params,
    }),
  });
}
